/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A3/utils/a3CarbonCharacterizationFactor.ts.
 */

import type {
  FormDataResponse,
  FormSubSection,
} from "../../../../../../interfaces/formInterfaces";
import type { CharacterizationFactorPublic } from "../../../../../../services/characterizationFactorsPublicService";
import { normalizeUnitToken } from "../../shared/fuelMixReferenceAutofill";
import { discoverA3FuelFieldNames } from "./a3FuelMixAutofill";
import {
  alignA3FuelsPerProcessRowCount,
  processFuelFlatStart,
} from "./a3ProcessFuelLayout";

const FUEL_CODE = "A3_PROCESS_FUEL_DETAILS";
const CARBON_CODE = "A3_EMBODIED_CARBON_DETAILS";

/** Same defaults as Form A1 when GET /characterization-factors is empty. */
const FUEL_CHARACTERIZATION_FACTORS: Record<
  string,
  { value: number; unit: string }
> = {
  coal: { value: 95, unit: "kg CO2/GJ" },
  diesel: { value: 74, unit: "kg CO2/GJ" },
  biofuel: { value: 56, unit: "kg CO2/GJ" },
  petrol: { value: 77, unit: "kg CO2/GJ" },
  electric: { value: 0.7, unit: "kg CO2/kWh" },
};

const normalizeFuelKey = (v?: string) =>
  v ? v.toLowerCase().replace(/\s+/g, "_") : "";

function parseLeadingNumberFromDisplay(s: string): number | null {
  const t = String(s ?? "").trim();
  const m = t.match(/^[-+]?\d*\.?\d+(?:[eE][-+]?\d+)?/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
}

function findPublicFactorForFuelMix(
  fuelMix: string | undefined,
  factors: CharacterizationFactorPublic[]
): CharacterizationFactorPublic | undefined {
  if (!factors.length) return undefined;
  const sel = String(fuelMix ?? "").trim();
  if (!sel) return undefined;
  const key = normalizeFuelKey(sel);
  const exact = factors.find((f) => normalizeFuelKey(f.fuelMix) === key);
  if (exact) return exact;
  const lower = sel.toLowerCase();
  return factors.find((f) => {
    const fk = (f.fuelMix || "").toLowerCase().trim();
    return (
      fk === lower ||
      fk.includes(lower) ||
      lower.includes(fk) ||
      normalizeFuelKey(f.fuelMix) === key
    );
  });
}

function resolveUnitValueForCarbonField(
  carbon: FormSubSection,
  unitFieldName: string,
  desiredUnitToken: string,
  unitIdByToken: Record<string, number>
): string {
  const token = String(desiredUnitToken || "").trim();
  if (!token) return "";

  const unitField = carbon.fields?.find((f: any) => f?.name === unitFieldName);
  const opts = unitField?.dropdown?.options || unitField?.dropdownValues || [];
  if (Array.isArray(opts) && opts.length > 0) {
    const target = normalizeUnitToken(token);
    const hit = opts.find((o: any) => {
      return (
        normalizeUnitToken(o?.label) === target ||
        normalizeUnitToken(o?.value) === target
      );
    });
    if (hit?.value !== undefined && hit?.value !== null) {
      return String(hit.value);
    }
  }

  const mappedId =
    unitIdByToken[String(token).trim().toLowerCase()] ||
    unitIdByToken[normalizeUnitToken(token)];
  if (mappedId) return String(mappedId);

  return "";
}

function parseNum(raw: string | undefined): number {
  const n = Number(String(raw ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : NaN;
}

/** Pick flat fuel row with largest quantity that has a fuel mix; ties favor lower index. */
export function pickRepresentativeA3FuelFlatIndexForCarbon(
  values: Record<string, string>,
  fuelNames: ReturnType<typeof discoverA3FuelFieldNames>,
  flatRowCount: number
): number | null {
  const mixField = fuelNames.mix;
  if (!mixField) return null;
  const qField = fuelNames.quantity ?? "fuelQuantity_A3_01";

  let bestIdx: number | null = null;
  let bestQty = -Infinity;
  for (let flat = 0; flat < flatRowCount; flat++) {
    const mix = String(
      values[`${mixField}_${FUEL_CODE}_row${flat}`] ?? ""
    ).trim();
    if (!mix) continue;
    const qty = parseNum(values[`${qField}_${FUEL_CODE}_row${flat}`]);
    const q = Number.isFinite(qty) && qty >= 0 ? qty : 0;
    if (bestIdx === null || q > bestQty) {
      bestIdx = flat;
      bestQty = q;
    }
  }
  if (bestIdx !== null) return bestIdx;

  for (let flat = 0; flat < flatRowCount; flat++) {
    const mix = String(
      values[`${mixField}_${FUEL_CODE}_row${flat}`] ?? ""
    ).trim();
    if (mix) return flat;
  }
  return null;
}

/** Representative fuel flat row within one manufacturing sub-process (for CF autofill on that carbon row). */
export function pickRepresentativeA3FuelFlatIndexForCarbonInProcess(
  values: Record<string, string>,
  fuelNames: ReturnType<typeof discoverA3FuelFieldNames>,
  fuelsPerProcess: number[],
  processIndex: number,
  processRowCount: number
): number | null {
  const mixField = fuelNames.mix;
  if (!mixField) return null;
  const qField = fuelNames.quantity ?? "fuelQuantity_A3_01";
  const counts = alignA3FuelsPerProcessRowCount(
    fuelsPerProcess,
    processRowCount
  );
  const start = processFuelFlatStart(processIndex, counts);
  const nFuels = Math.max(1, counts[processIndex] ?? 1);

  let bestIdx: number | null = null;
  let bestQty = -Infinity;
  for (let j = 0; j < nFuels; j++) {
    const flat = start + j;
    const mix = String(
      values[`${mixField}_${FUEL_CODE}_row${flat}`] ?? ""
    ).trim();
    if (!mix) continue;
    const qty = parseNum(values[`${qField}_${FUEL_CODE}_row${flat}`]);
    const q = Number.isFinite(qty) && qty >= 0 ? qty : 0;
    if (bestIdx === null || q > bestQty) {
      bestIdx = flat;
      bestQty = q;
    }
  }
  if (bestIdx !== null) return bestIdx;

  for (let j = 0; j < nFuels; j++) {
    const flat = start + j;
    const mix = String(
      values[`${mixField}_${FUEL_CODE}_row${flat}`] ?? ""
    ).trim();
    if (mix) return flat;
  }
  return null;
}

/** Exported function — discover A3Carbon Characterization Fields. */
export function discoverA3CarbonCharacterizationFields(
  formData: FormDataResponse | null
): { valueField: string; unitField?: string } | null {
  const sec = formData?.formSubSections?.find((s) => s.code === CARBON_CODE);
  if (!sec?.fields?.length) return null;
  const L = (s: string | undefined) => (s ?? "").toLowerCase();

  const valueField = sec.fields.find(
    (f) =>
      f.type !== "UNIT" &&
      L(f.name).includes("characterization") &&
      L(f.name).includes("factor") &&
      !L(f.name).includes("unit") &&
      !L(f.name).includes("reference") &&
      !L(f.name).includes("functional") &&
      !L(f.name).includes("rawmaterial")
  )?.name;

  const unitField = sec.fields.find(
    (f) =>
      f.type === "UNIT" &&
      L(f.name).includes("characterization") &&
      L(f.name).includes("factor")
  )?.name;

  if (!valueField) return null;
  return { valueField, unitField };
}

/**
 * Auto-fill A3 Embodied Carbon `characterizationFactor` / unit per sub-process row from that row's fuels
 * (same API + hardcoded fallback pattern as Form A1).
 */
export function buildA3CarbonCharacterizationFactorUpdates(
  formData: FormDataResponse | null,
  values: Record<string, string>,
  processRowCount: number,
  fuelsPerProcess: number[],
  apiFactors: CharacterizationFactorPublic[],
  manualByRow: Record<string, boolean>,
  unitIdByToken: Record<string, number>
): Record<string, string> {
  if (!formData || processRowCount <= 0) return {};

  const carbon = formData.formSubSections?.find((s) => s.code === CARBON_CODE);
  if (!carbon?.code) return {};

  const fields = discoverA3CarbonCharacterizationFields(formData);
  if (!fields?.valueField) return {};

  const fuelNames = discoverA3FuelFieldNames(formData);
  if (!fuelNames.mix) return {};

  const P = Math.max(1, processRowCount);
  const updates: Record<string, string> = {};
  const unitFieldName = fields.unitField;

  for (let p = 0; p < P; p++) {
    const rowKey = `row${p}`;
    if (manualByRow[rowKey]) continue;

    const repFlat = pickRepresentativeA3FuelFlatIndexForCarbonInProcess(
      values,
      fuelNames,
      fuelsPerProcess,
      p,
      P
    );

    const cfKey = `${fields.valueField}_${CARBON_CODE}_row${p}`;
    const unitValueKey = unitFieldName
      ? `${unitFieldName}_${CARBON_CODE}_row${p}`
      : null;

    if (repFlat == null) {
      updates[cfKey] = "";
      if (unitValueKey) updates[unitValueKey] = "";
      continue;
    }

    const mix = String(
      values[`${fuelNames.mix}_${FUEL_CODE}_row${repFlat}`] ?? ""
    ).trim();

    if (!mix) {
      updates[cfKey] = "";
      if (unitValueKey) updates[unitValueKey] = "";
      continue;
    }

    let numeric: number | null = null;
    let unitStr = "";

    if (apiFactors.length > 0) {
      const pub = findPublicFactorForFuelMix(mix, apiFactors);
      if (pub) {
        numeric = parseLeadingNumberFromDisplay(pub.characterizationFactor);
        unitStr = String(pub.unit ?? "").trim();
      }
    }

    if (numeric == null || !Number.isFinite(numeric)) {
      const key = normalizeFuelKey(mix);
      const factor = FUEL_CHARACTERIZATION_FACTORS[key];
      if (factor) {
        numeric = factor.value;
        unitStr = factor.unit;
      }
    }

    if (numeric == null || !Number.isFinite(numeric)) {
      continue;
    }

    updates[cfKey] = String(numeric);

    if (unitFieldName && unitValueKey) {
      const resolved = resolveUnitValueForCarbonField(
        carbon,
        unitFieldName,
        unitStr,
        unitIdByToken
      );
      if (resolved && values[unitValueKey] !== resolved) {
        updates[unitValueKey] = resolved;
      }
    }
  }

  return updates;
}

/** Numeric CF + unit label for a fuel mix (API first, then same hardcoded map as form autofill). */
export function resolveA3CharacterizationFactorForMix(
  mix: string,
  apiFactors: CharacterizationFactorPublic[]
): { value: number; unitLabel: string } | null {
  const trimmed = String(mix ?? "").trim();
  if (!trimmed) return null;

  let numeric: number | null = null;
  let unitStr = "";

  if (apiFactors.length > 0) {
    const pub = findPublicFactorForFuelMix(trimmed, apiFactors);
    if (pub) {
      numeric = parseLeadingNumberFromDisplay(pub.characterizationFactor);
      unitStr = String(pub.unit ?? "").trim();
    }
  }

  if (numeric == null || !Number.isFinite(numeric)) {
    const key = normalizeFuelKey(trimmed);
    const factor = FUEL_CHARACTERIZATION_FACTORS[key];
    if (factor) {
      numeric = factor.value;
      unitStr = factor.unit;
    }
  }

  if (numeric == null || !Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }
  return { value: numeric, unitLabel: unitStr };
}
